import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Paper
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { toast } from 'react-toastify';
import Order from '../../models/order';

const QRScanner = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const isScannerRunning = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [cameraId, setCameraId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (setPageTitle) {
      setPageTitle('Quét mã QR');
    }
  }, [setPageTitle]);

  useEffect(() => {
    const startScanning = async () => {
      try {
        // Initialize the scanner
        const html5QrCode = new Html5Qrcode("qr-reader");
        scannerRef.current = html5QrCode;

        const onScanSuccess = (decodedText, decodedResult) => {
          // Success callback
          console.log('QR Code scanned:', decodedText);
          setScanResult(decodedText);
          handleQRCode(decodedText);

          // Stop scanning after successful scan
          if (isScannerRunning.current) {
            html5QrCode.stop()
              .then(() => {
                isScannerRunning.current = false;
              })
              .catch(err => console.error('Error stopping scanner:', err));
          }
        };

        const onScanError = (errorMessage) => {
          // Error callback - ignore as they happen frequently during scanning
        };

        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        };

        try {
          // Try to use back camera only with exact constraint
          await html5QrCode.start(
            { facingMode: { exact: "environment" } },
            config,
            onScanSuccess,
            onScanError
          );

          isScannerRunning.current = true;
          setScanning(true);
          console.log('Started with exact back camera');
        } catch (exactError) {
          console.log('Exact back camera failed, trying ideal mode:', exactError.message);

          try {
            // Fallback: prefer back camera but allow front if back not available
            await html5QrCode.start(
              { facingMode: "environment" },
              config,
              onScanSuccess,
              onScanError
            );

            isScannerRunning.current = true;
            setScanning(true);
            console.log('Started with ideal back camera');
          } catch (idealError) {
            console.error('Both camera modes failed:', idealError);
            throw idealError;
          }
        }
      } catch (err) {
        console.error('Error starting scanner:', err);
        if (err.name === 'NotAllowedError') {
          setError('Quyền truy cập camera bị từ chối. Vui lòng cho phép truy cập camera trong cài đặt trình duyệt.');
        } else if (err.name === 'NotFoundError') {
          setError('Không tìm thấy camera sau trên thiết bị này.');
        } else if (err.name === 'NotReadableError') {
          setError('Camera đang được sử dụng bởi ứng dụng khác.');
        } else if (err.name === 'OverconstrainedError') {
          setError('Không tìm thấy camera sau. Vui lòng kiểm tra thiết bị của bạn.');
        } else {
          setError('Không thể khởi động camera: ' + err.message);
        }
      }
    };

    startScanning();

    // Cleanup function
    return () => {
      if (scannerRef.current && isScannerRunning.current) {
        scannerRef.current.stop()
          .then(() => {
            isScannerRunning.current = false;
          })
          .catch((err) => {
            // Silently handle errors during cleanup
            console.log('Scanner cleanup - scanner already stopped');
          });
      }
    };
  }, []);

  const handleQRCode = async (qrData) => {
    try {
      // Clean up QR data: remove .png extension and trim whitespace
      let cleanedData = qrData.trim();
      if (cleanedData.toLowerCase().endsWith('.png')) {
        cleanedData = cleanedData.slice(0, -4);
      }
      if (cleanedData.toLowerCase().endsWith('.jpg') || cleanedData.toLowerCase().endsWith('.jpeg')) {
        cleanedData = cleanedData.slice(0, cleanedData.lastIndexOf('.'));
      }
      cleanedData = cleanedData.trim();

      // Parse QR code format: <object>|<action>|<id>
      // Example: order|pack|1234
      const parts = cleanedData.split('|');

      // If QR code is just plain text/number (not pipe-separated format)
      // Treat it as order name and fetch order to get ID
      if (parts.length === 1) {
        const orderName = cleanedData;
        setLoading(true);

        try {
          // Fetch order by name
          const orderModel = new Order();
          orderModel.name = orderName;
          const response = await orderModel.getOrderByDate(1, 10);

          if (response && response.data && response.data.length > 0) {
            const order = response.data[0];
            toast.success(`Chuyển đến chi tiết đơn hàng ${orderName}`, {
              position: 'top-center',
              autoClose: 2000,
            });
            navigate(`/orders/${order.id}`);
          } else {
            setError(`Không tìm thấy đơn hàng với mã: ${orderName}`);
            toast.error(`Không tìm thấy đơn hàng với mã: ${orderName}`, {
              position: 'top-center',
              autoClose: 3000,
            });
          }
        } catch (err) {
          console.error('Error fetching order:', err);
          setError('Có lỗi xảy ra khi tìm đơn hàng. Vui lòng thử lại.');
          toast.error('Có lỗi xảy ra khi tìm đơn hàng', {
            position: 'top-center',
            autoClose: 3000,
          });
        } finally {
          setLoading(false);
        }
        return;
      }

      // Handle pipe-separated format
      if (parts.length !== 3) {
        setError('Định dạng mã QR không hợp lệ. Vui lòng thử lại.');
        toast.error('Định dạng mã QR không hợp lệ', {
          position: 'top-center',
          autoClose: 3000,
        });
        return;
      }

      const [object, action, identifier] = parts;

      // Handle different object types and actions
      switch (object.toLowerCase()) {
        case 'order':
          await handleOrderQR(action, identifier);
          break;
        case 'product':
          handleProductQR(action, identifier);
          break;
        case 'customer':
          handleCustomerQR(action, identifier);
          break;
        case 'package':
          handlePackageQR(action, identifier);
          break;
        default:
          setError(`Loại đối tượng "${object}" không được hỗ trợ`);
          toast.error(`Loại đối tượng "${object}" không được hỗ trợ`, {
            position: 'top-center',
            autoClose: 3000,
          });
      }
    } catch (err) {
      console.error('Error parsing QR code:', err);
      setError('Có lỗi xảy ra khi xử lý mã QR');
      toast.error('Có lỗi xảy ra khi xử lý mã QR', {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  };

  const handleOrderQR = async (action, orderIdentifier) => {
    setLoading(true);

    try {
      // Fetch order by name to get ID
      const orderModel = new Order();
      orderModel.name = orderIdentifier;
      const response = await orderModel.getOrderByDate(1, 10);

      if (!response || !response.data || response.data.length === 0) {
        setError(`Không tìm thấy đơn hàng với mã: ${orderIdentifier}`);
        toast.error(`Không tìm thấy đơn hàng với mã: ${orderIdentifier}`, {
          position: 'top-center',
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      const order = response.data[0];
      const orderId = order.id;

      switch (action.toLowerCase()) {
        case 'pack':
          toast.success(`Chuyển đến trang đóng gói đơn hàng ${orderIdentifier}`, {
            position: 'top-center',
            autoClose: 2000,
          });
          navigate(`/orders/${orderId}`);
          break;
        case 'view':
          toast.success(`Chuyển đến chi tiết đơn hàng ${orderIdentifier}`, {
            position: 'top-center',
            autoClose: 2000,
          });
          navigate(`/orders/${orderId}`);
          break;
        case 'invoice':
          toast.success(`Chuyển đến trang gửi hóa đơn ${orderIdentifier}`, {
            position: 'top-center',
            autoClose: 2000,
          });
          navigate(`/orders/${orderId}/send_invoice`);
          break;
        case 'payment':
          toast.success(`Chuyển đến trang thanh toán ${orderIdentifier}`, {
            position: 'top-center',
            autoClose: 2000,
          });
          navigate(`/orders/${orderId}/paid_proof`);
          break;
        default:
          // Default to order detail page
          toast.success(`Chuyển đến chi tiết đơn hàng ${orderIdentifier}`, {
            position: 'top-center',
            autoClose: 2000,
          });
          navigate(`/orders/${orderId}`);
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Có lỗi xảy ra khi tìm đơn hàng. Vui lòng thử lại.');
      toast.error('Có lỗi xảy ra khi tìm đơn hàng', {
        position: 'top-center',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProductQR = (action, productId) => {
    switch (action.toLowerCase()) {
      case 'view':
        toast.success(`Chuyển đến chi tiết sản phẩm ${productId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/products/edit/${productId}`);
        break;
      case 'edit':
        toast.success(`Chuyển đến trang chỉnh sửa sản phẩm ${productId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/products/edit/${productId}`);
        break;
      default:
        toast.success(`Chuyển đến chi tiết sản phẩm ${productId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/products/edit/${productId}`);
    }
  };

  const handleCustomerQR = (action, customerId) => {
    switch (action.toLowerCase()) {
      case 'view':
        toast.success(`Chuyển đến chi tiết khách hàng ${customerId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/customers/${customerId}`);
        break;
      default:
        toast.success(`Chuyển đến chi tiết khách hàng ${customerId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/customers/${customerId}`);
    }
  };

  const handlePackageQR = (action, packageId) => {
    switch (action.toLowerCase()) {
      case 'view':
        toast.success(`Chuyển đến chi tiết gói ${packageId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/user-packages/${packageId}`);
        break;
      default:
        toast.success(`Chuyển đến chi tiết gói ${packageId}`, {
          position: 'top-center',
          autoClose: 2000,
        });
        navigate(`/user-packages/${packageId}`);
    }
  };

  const handleReset = async () => {
    setError(null);
    setScanResult(null);
    setScanning(false);

    // Reload the page to restart the scanner
    window.location.reload();
  };

  return (
    <Container sx={{ mt: 3, mb: 5, px: { xs: 1, sm: 2, md: 3 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" mb={4} justifyContent="center">
            <QrCodeScannerIcon color="primary" sx={{ mr: 2, fontSize: '2.5rem' }} />
            <Typography variant="h4" sx={{ fontWeight: 'medium' }}>
              Quét mã QR
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Lưu ý:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • Hệ thống sẽ tự động sử dụng camera sau của điện thoại
            </Typography>
            <Typography variant="body2" component="div">
              • Trình duyệt sẽ yêu cầu quyền truy cập camera
            </Typography>
            <Typography variant="body2" component="div">
              • Hãy nhấn "Allow" hoặc "Cho phép" khi được hỏi
            </Typography>
            <Typography variant="body2" component="div">
              • Camera cần kết nối HTTPS (hoặc localhost) để hoạt động
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {scanResult && !error && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Đã quét thành công: {scanResult}
            </Alert>
          )}

          {loading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography>Đang tìm đơn hàng...</Typography>
              </Box>
            </Alert>
          )}

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            {!scanning && !error && !scanResult && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Đang khởi động camera...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Vui lòng cho phép truy cập camera khi được hỏi
                </Typography>
              </Box>
            )}

            {scanning && (
              <>
                <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', fontWeight: 'medium' }}>
                  Hướng camera vào mã QR để quét
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  Định dạng: object|action|id (Ví dụ: order|pack|1234)
                </Typography>
              </>
            )}

            <Box
              id="qr-reader"
              sx={{
                width: '100%',
                maxWidth: '600px',
                margin: '0 auto',
                minHeight: scanning ? '400px' : '0',
                '& video': {
                  width: '100%',
                  borderRadius: '8px',
                  border: '2px solid #1976d2'
                },
                '& canvas': {
                  display: 'none'
                }
              }}
            />
          </Paper>

          {(error || scanResult) && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleReset}
              >
                Quét lại
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Các loại mã QR được hỗ trợ:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Mã đơn giản:</strong> 123455 - Tự động chuyển đến chi tiết đơn hàng
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Đơn hàng:</strong> order|pack|[order_name] - Chuyển đến trang đóng gói
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Đơn hàng:</strong> order|view|[order_name] - Xem chi tiết đơn hàng
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Đơn hàng:</strong> order|invoice|[order_name] - Gửi hóa đơn
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Sản phẩm:</strong> product|view|[product_id] - Xem chi tiết sản phẩm
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Khách hàng:</strong> customer|view|[customer_id] - Xem chi tiết khách hàng
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Gói:</strong> package|view|[package_id] - Xem chi tiết gói
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default QRScanner;
